import { StatusCodes } from 'http-status-codes';
import moment from 'moment';
import React, { useContext, useState, createContext, useEffect } from 'react';
import { toastr } from 'react-redux-toastr';
import {
  Assignee,
  Dict,
  FilterValueType,
  getFilters,
  ICalendarEventResponse,
  IShowroomShift,
  IStaffLeave,
  IUpdateEventModel,
} from '..';
import api from '../../api';
import { ActivityModel } from '../../containers/leads/components/activities/add-activity';
import {
  IBigCalendarEvent,
  IBigCalendarResource,
} from '../components/calendar/big-calendar-component';
import {
  getMinimumTimeAndMaximumTime,
  IMinMaxDate,
  parseAssigneesBigCalendarResources,
  parseCalendarEventsToBigCalendarEvents,
  parseShowroomShifts,
  parseStaffLeaves,
  parseVehicleEventsToBigCalendarEvents,
  parseVehiclesBigCalendarResources,
} from '../components/calendar/calendar-mapper-utils';
import { IRadioButton } from '../components/radio-button/radio-button';

export const views = ['day', 'week', 'month'];

export interface ICalendarContextData {
  currentView: string;
  setCurrentView: (value: string) => void;
  children?: any;
  viewButtons: IRadioButton[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  events: IBigCalendarEvent[];
  setEvents: (events: IBigCalendarEvent[]) => void;
  resources: IBigCalendarResource[];
  setResources: (resources: IBigCalendarResource[]) => void;
  getCalendarEvents: () => void;
  updateEditedEvent: (event: IBigCalendarEvent, index: number) => void;
  onEditEvent: (
    eventId: string,
    event: IUpdateEventModel,
    onSuccess?: () => void
  ) => void;
  currentActivity: ActivityModel | undefined;
  setCurrentActivity: (activity: ActivityModel | undefined) => void;
  calendarFilters: Dict<FilterValueType>;
  setCalendarFilters: (value: Dict<FilterValueType>) => void;
  filterKeys: IFilterKeys;
  resourceView: 'staff' | 'vehicle' | string;
  setResourceView: (value: 'staff' | 'vehicle' | string) => void;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  minMax: IMinMaxDate;
  listSummary: IEventListSummary;
  eventMarkAsDone: (
    eventId: string,
    onSuccess?: () => void,
    mileageIn?: string,
    mileageOut?: string
  ) => void;
  staffLeave: { [id: string]: IStaffLeave[] };
  showroomShifts: {
    [id: string]: IShowroomShift[];
  };
}
export const postsContextDefaultValue: ICalendarContextData = {
  currentView: 'day',
  setCurrentView: (value) => null,
  viewButtons: [],
  selectedDate: new Date(),
  setSelectedDate: (date) => null,
  events: [],
  setEvents: (events) => null,
  resources: [],
  setResources: (resources) => null,
  getCalendarEvents: () => null,
  updateEditedEvent: (event, index) => null,
  currentActivity: undefined,
  setCurrentActivity: (activity) => null,
  onEditEvent: (eventId, event) => null,
  calendarFilters: {},
  staffLeave: {},

  minMax: {
    min: moment(new Date()).startOf('day').toDate(),
    max: moment(new Date()).endOf('day').toDate(),
  },
  setCalendarFilters: (value) => null,
  filterKeys: {
    salesPerson: 'assignedPersonIds',
    showroomIds: 'showroomIds',
    todo: 'todo',
    overdue: 'overdue',
    createdUserIds: 'createdUserIds',
    statuses: 'statuses',
  },
  resourceView: 'staff',
  setResourceView: (value) => null,
  isLoading: false,
  setIsLoading: (value) => null,
  eventMarkAsDone: (
    eventId: string,
    onSuccess?: () => void,
    mileageIn?: string,
    mileageOut?: string
  ) => null,
  listSummary: {},
  showroomShifts: {},
};

export interface IFilterKeys {
  salesPerson: string;
  showroomIds: string;
  todo: string;
  overdue: string;
  createdUserIds: string;
  activityTypes?: string;
  statuses?: string;
}

export const CalendarContext = createContext<ICalendarContextData>(
  postsContextDefaultValue
);

export const CalendarProvider = ({ children }: any) => {
  const filterKeys: IFilterKeys = {
    salesPerson: 'assignedPersonIds',
    showroomIds: 'showroomIds',
    todo: 'todo',
    overdue: 'overdue',
    createdUserIds: 'createdUserIds',
    activityTypes: 'activityTypes',
    statuses: 'statuses',
  };
  const [currentView, setCurrentView] = useState<string>('day');
  const [listSummary, setListSummary] = useState<IEventListSummary>({});
  const [resourceView, setResourceView] = useState<
    'staff' | 'vehicle' | string
  >('staff');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [events, setEvents] = useState<IBigCalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resources, setResources] = useState<IBigCalendarResource[]>([]);
  const [currentActivity, setCurrentActivity] = React.useState<ActivityModel>();
  const [staffLeave, setStaffLeave] = useState<{ [id: string]: IStaffLeave[] }>(
    {}
  );

  const [showroomShifts, setShowroomShifts] = useState<{
    [id: string]: IShowroomShift[];
  }>({});
  const [minMax, setMinMax] = React.useState<IMinMaxDate>({
    min: moment(new Date()).startOf('day').toDate(),
    max: moment(new Date()).endOf('day').toDate(),
  });
  const [calendarFilters, setCalendarFilters] = useState<Dict<FilterValueType>>(
    {}
  );
  useEffect(() => {
    getCalendarEvents();
  }, [calendarFilters, currentView, selectedDate, resourceView]);
  const viewButtons: IRadioButton[] = [
    {
      key: 'day',
      disaplayText: 'Day',
    },
    {
      key: 'week',
      disaplayText: 'Week',
    },
    {
      key: 'month',
      disaplayText: 'Month',
    },
  ];
  const leadService = api.lead;

  const createCalendarEventFilter = (): ICalendarFilter => {
    const startDate = moment(selectedDate)
      .startOf(currentView as any)
      .toDate();
    const endDate = moment(selectedDate)
      .endOf(currentView as any)
      .toDate();
    const assignedPersonFilter: string[] = getFilters(calendarFilters)[
      filterKeys.salesPerson
    ];
    const showroomFilter: string[] = getFilters(calendarFilters)[
      filterKeys.showroomIds
    ];
    const activityTypesFilter: string[] = filterKeys.activityTypes
      ? getFilters(calendarFilters)[filterKeys.activityTypes]
      : [];
    const createdUserFilter: string[] = filterKeys.createdUserIds
      ? getFilters(calendarFilters)[filterKeys.createdUserIds]
      : [];
    const statusesFilter: string[] = filterKeys.statuses
      ? getFilters(calendarFilters)[filterKeys.statuses]
      : [];

    return {
      fromDate: startDate,
      toDate: endDate,
      assignedPersonIds: assignedPersonFilter,
      showroomIds: showroomFilter,
      activityTypes: activityTypesFilter,
      createdUserIds: createdUserFilter,
      statuses: statusesFilter,
      dateType: 'Due Date',
    };
  };

  const getCalendarEvents = async (onSuccess?: (events: any) => void) => {
    if (resourceView === 'staff') {
      getCalendarEventList(onSuccess);
    } else {
      getVehicleEvents(onSuccess);
    }
  };

  const getCalendarEventList = async (onSuccess?: (events: any) => void) => {
    let tempResources: Assignee[] = [];
    if (calendarFilters[filterKeys.salesPerson] != null) {
      const fltr: any = calendarFilters[filterKeys.salesPerson];

      tempResources = fltr.map((p: any) => {
        return { _id: p.value, name: p.label };
      });
    }
    setIsLoading(true);
    try {
      const filter: ICalendarFilter = createCalendarEventFilter();
      const response = await leadService.getCalandarEvents(filter);
      if (response.status === StatusCodes.OK) {
        const summary: IEventListSummary = {
          appointments: response.appointments,
          cancelled: response.cancelled,
          completed: response.completed,
          noshow: response.noshow,
          rescheduled: response.rescheduled,
        };
        const leaves = parseStaffLeaves(response.data.staffLeaves ?? []);

        setStaffLeave(leaves);
        const showroomshifts = parseShowroomShifts(
          response.data.showroomTimings ?? [],
          filter
        );
        setShowroomShifts(showroomshifts);

        setListSummary(summary);
        const eventDetails: ICalendarEventResponse = response.data;
        setEvents(
          parseCalendarEventsToBigCalendarEvents(eventDetails.appointments)
        );
        setMinMax(getMinimumTimeAndMaximumTime(response.data));
        if (tempResources.length === 0) {
          tempResources = eventDetails.assignees ?? [];
        }
        setResources(
          parseAssigneesBigCalendarResources(tempResources, filter.showroomIds)
        );
      } else {
        toastr.error(
          'Error',
          response.message ?? 'Unable to fetch calendar events'
        );
      }
    } catch (err) {
      toastr.error('Error', 'Something went wrong while fetching the events');
    } finally {
      setIsLoading(false);
    }
  };

  const eventMarkAsDone = async (
    eventId: string,
    onSuccess?: () => void,
    mileageIn?: string,
    mileageOut?: string
  ) => {
    setIsLoading(true);
    try {
      const response = await leadService.markEventAsDone(
        eventId,
        mileageIn,
        mileageOut
      );
      if (response.status === StatusCodes.OK) {
        toastr.success('Success', 'Activity marked as done');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error('Error', response.message ?? 'Unable to mark as done');
      }
    } catch (err) {
      toastr.error(
        'Error',
        (err as any).response?.data?.data?.message ||
          'Something went wrong while marking as done'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getVehicleEvents = async (onSuccess?: (events: any) => void) => {
    try {
      setIsLoading(true);
      const filter = createCalendarEventFilter();
      const response = await leadService.getVehicleEvents(filter);
      if (response.status === StatusCodes.OK) {
        const summary: IEventListSummary = {
          appointments: response.appointments,
          cancelled: response.cancelled,
          completed: response.completed,
          noshow: response.noshow,
          rescheduled: response.rescheduled,
        };
        setListSummary(summary);
        const eventDetails: ICalendarEventResponse = response.data;
        setEvents(
          parseVehicleEventsToBigCalendarEvents(eventDetails.appointments)
        );
        setResources(parseVehiclesBigCalendarResources(eventDetails.vehicles));
      } else {
        toastr.error(
          'Error',
          response.message ?? 'Unable to fetch vehicle events'
        );
      }
    } catch (err) {
      toastr.error(
        'Error',
        'Something went wrong while fetching the vehicle events'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onEditEvent = async (
    eventId: string,
    data: IUpdateEventModel,
    onSuccess?: () => void
  ) => {
    try {
      const response = await leadService.updateLeadActivity(eventId, data);
      if (response.status === StatusCodes.OK) {
        toastr.success('Success', 'Activity updated');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error(
          'Error',
          response.message ?? 'Unable to update the activity'
        );
      }
    } catch (err) {
      toastr.error(
        'Error',
        (err as any).response?.data?.data?.message ||
          'Something went wrong while updating activity'
      );
    }
  };

  const updateEvent = async (
    updatedEvent: IBigCalendarEvent,
    idx: number,
    onSuccess?: () => void
  ) => {
    if (resourceView === 'staff') {
      updateCalendarEvent(updatedEvent, idx, onSuccess);
    } else {
      updateVehicleEvent(updatedEvent, idx, onSuccess);
    }
  };

  const updateVehicleEvent = async (
    updatedEvent: IBigCalendarEvent,
    idx: number,
    onSuccess?: () => void
  ) => {
    const apiModel: IUpdateEventModel = {
      startDate: updatedEvent.start,
      endDate: updatedEvent.end,
      vehicleModelId: updatedEvent.resourceId,
    };
    const resourceDetail = resources.find(
      (p) => p.resourceId === updatedEvent.resourceId
    );
    if (resourceDetail != null) {
      updatedEvent.appointment.vehicleModel = {
        _id: resourceDetail.resourceId,
        name: resourceDetail.resourceTitle,
      };
    }
    const nextEvents = [...events];
    nextEvents.splice(idx, 1, updatedEvent);
    setEvents(nextEvents);
    updateEventInServer(updatedEvent.id, apiModel, onSuccess);
  };

  const updateCalendarEvent = async (
    updatedEvent: IBigCalendarEvent,
    idx: number,
    onSuccess?: () => void
  ) => {
    const apiModel: IUpdateEventModel = {
      startDate: updatedEvent.start,
      endDate: updatedEvent.end,
      assignedPersonId: updatedEvent.resourceId,
    };
    const resourceDetail = resources.find(
      (p) => p.resourceId === updatedEvent.resourceId
    );
    if (resourceDetail != null) {
      updatedEvent.appointment.assignedPerson = {
        _id: resourceDetail.resourceId,
        name: resourceDetail.resourceTitle,
        showroom: resourceDetail.showroom,
      };
    }
    const nextEvents = [...events];
    nextEvents.splice(idx, 1, updatedEvent);
    setEvents(nextEvents);
    updateEventInServer(updatedEvent.id, apiModel, onSuccess);
  };

  const updateEventInServer = async (
    id: string,
    apiModel: IUpdateEventModel,
    onSuccess?: () => void
  ) => {
    try {
      const response = await leadService.updateLeadActivity(id, apiModel);
      if (response.status === StatusCodes.OK) {
        toastr.success('Success', 'Event updated');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error(
          'Error',
          response.message ?? 'Unable to update the activity'
        );
      }
    } catch (err) {
      toastr.error(
        'Error',
        (err as any).response?.data?.data?.message ||
          'Something went wrong while updating activity'
      );
    }
  };

  return (
    <CalendarContext.Provider
      value={{
        currentView,
        listSummary,
        setCurrentView,
        viewButtons,
        selectedDate,
        setSelectedDate,
        events,
        setEvents,
        resources,
        setResources,
        getCalendarEvents,
        updateEditedEvent: updateEvent,
        currentActivity,
        setCurrentActivity,
        onEditEvent,
        calendarFilters,
        setCalendarFilters,
        filterKeys,
        resourceView,
        setResourceView,
        isLoading,
        setIsLoading,
        minMax,
        eventMarkAsDone,
        staffLeave,
        showroomShifts,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = () => useContext(CalendarContext);

export interface IEventListSummary {
  appointments?: number;
  cancelled?: number;
  completed?: number;
  noshow?: number;
  rescheduled?: number;
}

export interface ICalendarFilter {
  fromDate: Date;
  toDate: Date;
  assignedPersonIds: string[];
  showroomIds: string[];
  activityTypes: string[];
  createdUserIds: string[];
  statuses?: string[];
  dateType?: string;
}
